import { useState, useEffect, useCallback } from 'react';
import { payoutsService, tradersService } from '@/services';
import type { PayoutInvoiceListItem, PayoutInvoiceStatus } from '@/types/payout';
import type { Trader } from '@/types/trader';
import { useNotification } from '@/contexts/NotificationContext';

export interface PayoutInvoiceFiltersState {
  /** Пустая строка — все статусы (параметр status не передаётся). */
  status: '' | PayoutInvoiceStatus;
  /** Пустая строка — без фильтра payout_id. */
  payout_id: string;
  /** Пустая строка — без фильтра trader_id. */
  trader_id: string;
  page: number;
  per_page: number;
}

const defaultFilters: PayoutInvoiceFiltersState = {
  status: '',
  payout_id: '',
  trader_id: '',
  page: 1,
  per_page: 20,
};

export type UsePayoutInvoicesOptions = {
  /** Зафиксированные статусы в запросе (без UI-фильтра); например только `IN_PROGRESS`. */
  fixedStatuses?: PayoutInvoiceStatus[];
  /** Размер страницы (по умолчанию 20; макс. API — 500). */
  perPage?: number;
};

export function usePayoutInvoices(options?: UsePayoutInvoicesOptions) {
  const fixedStatuses = options?.fixedStatuses;
  const perPage = options?.perPage ?? defaultFilters.per_page;
  const [invoices, setInvoices] = useState<PayoutInvoiceListItem[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<PayoutInvoiceFiltersState>(() => ({
    ...defaultFilters,
    per_page: perPage,
  }));
  const { showNotification } = useNotification();

  const loadData = useCallback(
    async (custom?: PayoutInvoiceFiltersState) => {
      const request = custom ?? filters;

      setLoading(true);
      try {
        let req = request;
        const p = req.page ?? 1;
        const pp = req.per_page ?? perPage;
        const params: Parameters<typeof payoutsService.getPayoutInvoices>[0] = {
          limit: pp,
          offset: (p - 1) * pp,
        };
        if (fixedStatuses?.length) {
          params.status = fixedStatuses;
        } else if (req.status) {
          params.status = [req.status];
        }
        const payoutId = req.payout_id.trim();
        const traderId = req.trader_id.trim();
        if (payoutId) {
          params.payout_id = payoutId;
        }
        if (traderId) {
          params.trader_id = traderId;
        }

        let response = await payoutsService.getPayoutInvoices(params);
        const serverTotalPages = Math.max(1, response.total_pages);

        if (p > serverTotalPages && response.total_pages > 0) {
          req = { ...req, page: serverTotalPages };
          setFilters(req);
          response = await payoutsService.getPayoutInvoices({
            ...params,
            offset: (serverTotalPages - 1) * pp,
          });
        } else if (response.items.length === 0 && p > 1) {
          const prevPage = p - 1;
          req = { ...req, page: prevPage };
          setFilters(req);
          response = await payoutsService.getPayoutInvoices({
            ...params,
            offset: (prevPage - 1) * pp,
          });
        }

        setInvoices(response.items);
        setTotalPages(Math.max(1, response.total_pages));
        setError(null);
      } catch {
        setError('Ошибка при загрузке данных');
        setInvoices([]);
        showNotification('Ошибка при загрузке данных', 'error');
      } finally {
        setLoading(false);
      }
    },
    [filters, fixedStatuses, perPage, showNotification],
  );

  useEffect(() => {
    const loadTraders = async () => {
      try {
        const tradersData = await tradersService.getTraders({ is_archive: false });
        setTraders(tradersData);
      } catch {
        showNotification('Ошибка при загрузке справочника трейдеров', 'error');
      }
    };
    loadTraders();
  }, [showNotification]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- начальная загрузка
  }, []);

  const handleFilterChange = (field: keyof PayoutInvoiceFiltersState, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? (value as number) : 1,
    }));
  };

  const handleSearch = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    loadData(next);
  };

  const handleReset = () => {
    if (fixedStatuses?.length) {
      const reset = { ...filters, payout_id: '', trader_id: '', page: 1, per_page: perPage };
      setFilters(reset);
      setTotalPages(1);
      loadData(reset);
      return;
    }
    const reset = { ...defaultFilters, per_page: perPage };
    setFilters(reset);
    setTotalPages(1);
    loadData(reset);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    const next = { ...filters, page: value };
    setFilters(next);
    loadData(next);
  };

  const handleRefresh = () => {
    loadData(filters);
  };

  return {
    invoices,
    traders,
    error,
    loading,
    totalPages,
    filters,
    handleFilterChange,
    handleSearch,
    handleReset,
    handlePageChange,
    handleRefresh,
    loadData,
  };
}
